import React, { useState, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import ColorModeContext from "../../layout/themeContext";
import { openApi } from "../../services/api";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#0a0a0a",
    },
    card: {
        width: "100%",
        maxWidth: 420,
        backgroundColor: "#111",
        borderRadius: 20,
        padding: "40px 32px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    },
    title: {
        color: "#fff",
        fontWeight: 700,
        fontSize: "1.4rem",
        marginBottom: 8,
        textAlign: "center",
    },
    subtitle: {
        color: "#9ca3af",
        fontSize: "0.85rem",
        marginBottom: 24,
        textAlign: "center",
    },
    inputLabel: {
        color: "#d1d5db",
        fontSize: "0.82rem",
        fontWeight: 600,
        marginBottom: 6,
        display: "block",
    },
    textField: {
        marginBottom: 20,
        '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: "rgba(255,255,255,0.06)",
            color: "#fff",
            '& fieldset': { borderColor: "rgba(255,255,255,0.12)" },
            '&:hover fieldset': { borderColor: "rgba(255,255,255,0.25)" },
            '&.Mui-focused fieldset': { borderColor: "#6366f1" },
        },
        '& .MuiInputBase-input': { color: "#fff" },
    },
    submitButton: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 700,
        padding: "10px 0",
        fontSize: "0.95rem",
        backgroundColor: "#6366f1",
        color: "#fff",
        marginBottom: 16,
        '&:hover': { backgroundColor: "#4f46e5" },
    },
    backLink: {
        color: "#6366f1",
        fontSize: "0.85rem",
        textAlign: "center",
        cursor: "pointer",
        display: "block",
        '&:hover': { textDecoration: "underline" },
    },
    successBox: {
        textAlign: "center",
        color: "#10b981",
        fontSize: "0.9rem",
        padding: "16px",
        backgroundColor: "rgba(16,185,129,0.1)",
        borderRadius: 10,
        marginBottom: 16,
    },
}));

const ResetPassword = () => {
    const classes = useStyles();
    const history = useHistory();
    const { token } = useParams();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName } = colorMode;
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password.length < 6) {
            toast.error("A senha deve ter no mínimo 6 caracteres.");
            return;
        }

        if (password !== confirmPassword) {
            toast.error("As senhas não coincidem.");
            return;
        }

        setLoading(true);
        try {
            await openApi.post("/auth/reset-password", { token, password });
            setSuccess(true);
            toast.success("Senha redefinida com sucesso!");
        } catch (err) {
            const msg = err?.response?.data?.error || "Token inválido ou expirado.";
            toast.error(msg);
        }
        setLoading(false);
    };

    return (
        <>
            <Helmet>
                <title>Redefinir Senha - {appName || "Sistema"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>
            <div className={classes.root}>
                <div className={classes.card}>
                    <Typography className={classes.title}>
                        Redefinir Senha
                    </Typography>
                    <Typography className={classes.subtitle}>
                        {success
                            ? "Sua senha foi alterada com sucesso!"
                            : "Digite sua nova senha"}
                    </Typography>

                    {success ? (
                        <>
                            <div className={classes.successBox}>
                                ✅ Senha redefinida! Agora você pode fazer login com sua nova senha.
                            </div>
                            <span
                                className={classes.backLink}
                                onClick={() => history.push("/login")}
                            >
                                Ir para o Login
                            </span>
                        </>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <label className={classes.inputLabel}>Nova Senha</label>
                            <TextField
                                variant="outlined"
                                required
                                fullWidth
                                placeholder="Mínimo 6 caracteres"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                type={showPassword ? "text" : "password"}
                                size="small"
                                className={classes.textField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword((s) => !s)}
                                                edge="end"
                                                size="small"
                                                style={{ color: "#9ca3af" }}
                                            >
                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <label className={classes.inputLabel}>Confirmar Senha</label>
                            <TextField
                                variant="outlined"
                                required
                                fullWidth
                                placeholder="Repita a senha"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                type={showPassword ? "text" : "password"}
                                size="small"
                                className={classes.textField}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                className={classes.submitButton}
                                disabled={loading}
                            >
                                {loading ? "Redefinindo..." : "Redefinir Senha"}
                            </Button>
                            <span
                                className={classes.backLink}
                                onClick={() => history.push("/login")}
                            >
                                Voltar ao Login
                            </span>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

export default ResetPassword;
